/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 10;

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   * @param {string} [legacyHandle] - The legacy handle to remove when the same product was stored previously.
   */
  static addProduct(productId, legacyHandle = '') {
    if (!productId) return;

    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter(
      (/** @type {string} */ id) => id !== productId && id !== legacyHandle
    );
    viewedProducts.unshift(productId);
    this.replaceProducts(viewedProducts);
  }

  static clearProducts() {
    try {
      localStorage.removeItem(this.#STORAGE_KEY);
    } catch {
      return;
    }
  }

  /**
   * Replaces the recently viewed products list.
   * @param {string[]} productIds - Product IDs ordered from newest to oldest.
   */
  static replaceProducts(productIds) {
    const normalizedProducts = [...new Set(productIds.filter((id) => typeof id === 'string' && id.length > 0))].slice(
      0,
      this.#MAX_PRODUCTS
    );

    try {
      localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(normalizedProducts));
    } catch {
      return;
    }
  }

  /**
   * Retrieves the list of recently viewed products from session storage.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    try {
      const storedProducts = JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
      if (!Array.isArray(storedProducts)) return [];

      return storedProducts
        .filter((id) => typeof id === 'string' && id.length > 0)
        .slice(0, this.#MAX_PRODUCTS);
    } catch {
      return [];
    }
  }
}

class RecentViewedProducts extends HTMLElement {
  constructor() {
    super();
    this.observer = null;
    this.abortController = null;
    this.hasLoaded = false;
    this.handleArrowClick = this.handleArrowClick.bind(this);
    this.updateArrows = this.updateArrows.bind(this);
  }

  connectedCallback() {
    this.closest('.recent-viewed-carousel')?.addEventListener('click', this.handleArrowClick);
    this.addEventListener('scroll', this.updateArrows, { passive: true });
    window.addEventListener('resize', this.updateArrows, { passive: true });

    if (!('IntersectionObserver' in window)) {
      this.loadProducts();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0]?.isIntersecting) return;

        observer.disconnect();
        this.loadProducts();
      },
      { rootMargin: '1200px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.abortController?.abort();
    this.closest('.recent-viewed-carousel')?.removeEventListener('click', this.handleArrowClick);
    this.removeEventListener('scroll', this.updateArrows);
    window.removeEventListener('resize', this.updateArrows);
  }

  async loadProducts() {
    if (this.hasLoaded) return;
    this.hasLoaded = true;
    this.setState('loading');

    try {
      const numericIdPattern = /^\d+$/;
      const handlePattern = /^[a-z0-9][a-z0-9-]*$/i;
      const storedProducts = RecentlyViewed.getProducts().filter(
        (value) => numericIdPattern.test(value) || handlePattern.test(value)
      );
      const currentProductId = this.dataset.currentProductId;
      const currentProductHandle = this.dataset.currentProductHandle;
      const maxProducts = Math.min(Number(this.dataset.maxProducts) || 10, 10);
      const visibleProducts = storedProducts
        .filter((value) => value !== currentProductId && value !== currentProductHandle)
        .slice(0, maxProducts);

      if (visibleProducts.length === 0) {
        this.setState('empty');
        return;
      }

      this.abortController = new AbortController();
      const url = new URL(Theme.routes.predictive_search_url, location.origin);
      url.searchParams.set(
        'q',
        visibleProducts.map((value) => (numericIdPattern.test(value) ? `id:${value}` : `handle:${value}`)).join(' OR ')
      );
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', String(maxProducts));
      url.searchParams.set('section_id', this.dataset.sectionId);

      const response = await fetch(url.toString(), {
        signal: this.abortController.signal,
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) throw new Error(`Recently viewed request failed: ${response.status}`);

      const responseDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
      const resultWrappers = [...responseDocument.querySelectorAll('[data-recent-product-id]')];
      const cardsByProduct = new Map();
      const canonicalIdsByProduct = new Map();
      for (const wrapper of resultWrappers) {
        const card = wrapper.querySelector('product-card');
        if (!card) continue;

        const productId = wrapper.dataset.recentProductId;
        const productHandle = wrapper.dataset.recentProductHandle;
        cardsByProduct.set(productId, card);
        cardsByProduct.set(productHandle, card);
        canonicalIdsByProduct.set(productId, productId);
        canonicalIdsByProduct.set(productHandle, productId);
      }
      const fragment = document.createDocumentFragment();
      const insertedProductIds = new Set();

      for (const productValue of visibleProducts) {
        const card = cardsByProduct.get(productValue);
        const canonicalProductId = canonicalIdsByProduct.get(productValue);
        if (!card || !canonicalProductId || insertedProductIds.has(canonicalProductId)) continue;

        insertedProductIds.add(canonicalProductId);
        this.optimizeCard(card);
        fragment.append(card);
      }

      RecentlyViewed.replaceProducts(
        storedProducts
          .map((productValue) => canonicalIdsByProduct.get(productValue))
          .filter((productId) => typeof productId === 'string')
      );

      if (!fragment.childNodes.length) {
        this.setState('empty');
        return;
      }

      this.replaceChildren(fragment);
      this.setState('loaded');
      this.updateArrows();
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      this.setState('empty');
    }
  }

  optimizeCard(card) {
    for (const image of card.querySelectorAll('img')) {
      image.loading = 'lazy';
      image.decoding = 'async';
      image.fetchPriority = 'low';
      image.sizes = '(max-width: 749px) 67vw, 25vw';

      const source = image.getAttribute('src');
      if (!source) continue;

      const imageUrl = new URL(source, location.origin);
      imageUrl.searchParams.set('width', '600');
      image.src = imageUrl.toString();
    }
  }

  handleArrowClick(event) {
    const arrow = event.target.closest('.recent-viewed-arrow');
    if (!arrow || arrow.disabled) return;

    const direction = Number(arrow.dataset.direction);
    this.scrollBy({ left: direction * this.clientWidth, behavior: 'smooth' });
  }

  updateArrows() {
    const carousel = this.closest('.recent-viewed-carousel');
    if (!carousel) return;

    const previousButton = carousel.querySelector('.recent-viewed-arrow--previous');
    const nextButton = carousel.querySelector('.recent-viewed-arrow--next');
    const maximumScroll = Math.max(this.scrollWidth - this.clientWidth, 0);
    const currentScroll = Math.abs(this.scrollLeft);

    if (previousButton) previousButton.disabled = currentScroll <= 1;
    if (nextButton) nextButton.disabled = currentScroll >= maximumScroll - 1;
  }

  setState(state) {
    const section = this.closest('.recent-viewed-main');
    if (!section) return;

    section.dataset.recentViewedState = state;
    section.setAttribute('aria-busy', state === 'idle' || state === 'loading' ? 'true' : 'false');
  }
}

if (!customElements.get('recent-viewed')) {
  customElements.define('recent-viewed', RecentViewedProducts);
}
